import { type ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface BulkActionsBarProps {
  /** Number of selected items. The bar slides up when > 0 and down when 0. */
  count: number;
  onClear: () => void;
  children: ReactNode;
  /** Override the singular/plural noun in the count label (defaults to "selected"). */
  noun?: string;
  className?: string;
}

/**
 * Shared selection toolbar used by Companies and Contacts. The shell shrinks
 * to fit its contents (no fixed width), wraps to multiple rows on narrow
 * viewports, and never extends beyond the viewport edges.
 */
export function BulkActionsBar({
  count,
  onClear,
  children,
  noun = "selected",
  className,
}: BulkActionsBarProps) {
  const visible = count > 0;

  return (
    <div
      role="region"
      aria-label="Bulk actions"
      aria-hidden={!visible}
      // The wrapper itself is centered via left-1/2 + translateX so the bar
      // shrinks to its own content. We can't use the more familiar
      // `inset-x-0 flex justify-center` because that would force the inner
      // card to stretch when combined with `w-full`.
      className={cn(
        "pointer-events-none fixed bottom-6 left-1/2 z-30 -translate-x-1/2",
        "transition-transform duration-200 ease-out",
        visible ? "translate-y-0" : "translate-y-[150%]",
        className,
      )}
      style={{ maxWidth: "calc(100vw - 48px)" }}
    >
      <div
        className={cn(
          "flex w-auto max-w-full flex-wrap items-center gap-2 rounded-plane border border-slate-900/[0.06] bg-white px-4 py-3 shadow-elevated",
          visible
            ? "pointer-events-auto opacity-100"
            : "pointer-events-none opacity-0",
          "transition-opacity duration-200 ease-out",
        )}
      >
        <span className="shrink-0 whitespace-nowrap text-sm font-semibold text-indigo-600">
          {count} {noun}
        </span>

        <span className="hidden h-4 w-px shrink-0 bg-slate-200 sm:inline-block" />

        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {children}
        </div>

        <span className="ml-auto h-4 w-px shrink-0 bg-slate-200" />

        <button
          type="button"
          onClick={onClear}
          aria-label="Clear selection"
          className="shrink-0 rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
